/**
 * Shared style tokens for sidebar rows.
 *
 * Both `SidebarItem` (leaf row) and `SidebarGroup` (collapsible header) use
 * these. The reference design (`new-agent-numeo-main/src/App.tsx` NavGroup
 * / NavChild) keeps all labels and icons at the same foreground colour in
 * every state — only the BACKGROUND changes on active / hover. Importantly,
 * a group header with children NEVER takes the active background when one
 * of its children is selected (reference line 270/299: `isLeafActive` is
 * `!hasChildren && activeItem === group.label`). So we expose a distinct
 * `ROW_HEADER` token for collapsible group headers.
 *
 * Tokens are the numeo-ds-v2 semantic CSS-variable utilities (`bg-accent-soft`,
 * `text-foreground`, etc.) — they self-flip for dark mode, no `dark:` pairs.
 */

/**
 * Shared row layout: 36px tall, 12px corner radius, 8px horizontal padding.
 *
 * `shrink-0` is load-bearing: rows live inside the scrolling `<nav>` (a flex
 * column with a definite height), and a plain `h-9` sets the flex-basis, not
 * a floor — without `shrink-0` the flexbox algorithm compresses every row
 * proportionally once total content exceeds the nav's height, instead of
 * leaving rows alone and letting the nav's own `overflow-y-auto` scroll.
 */
export const ROW_BASE = 'group flex h-9 w-full shrink-0 items-center gap-2 rounded-xl px-2 text-sm font-medium transition-colors'

/**
 * Active leaf row — accent-soft background, foreground text kept dark
 * (reference NavChild line 386: `color: 'var(--numeo-foreground)'`).
 * Text DOES NOT turn blue in the active state.
 */
export const ROW_ACTIVE = 'bg-accent-soft text-foreground'

/**
 * Idle text + hover background — `bg-default`, matching the footer utility
 * row's (Email/Notifications/Downloads) hover treatment for consistency
 * across every interactive row in the sidebar.
 */
export const ROW_IDLE = 'text-foreground hover:bg-default'

/**
 * Collapsible group header — always uses the idle treatment, even when one
 * of its children is the active route. Prevents Dashboard/Accounting/etc.
 * from lighting up blue when you click one of their sub-items.
 */
export const ROW_HEADER = ROW_IDLE

/** Disabled state. */
export const ROW_DISABLED = 'cursor-not-allowed text-foreground-muted opacity-50'
